import { AlertCircle, WifiOff, RefreshCw, ServerOff } from 'lucide-react'
import { Button } from './Button'
import { ApiError } from '@/lib/http/client'

interface ErrorDisplayProps {
  error: Error | ApiError | null
  title?: string
  onRetry?: () => void
  compact?: boolean
}

export function ErrorDisplay({ error, title, onRetry, compact = false }: ErrorDisplayProps) {
  if (!error) return null

  const isApiError = error instanceof ApiError
  const isCorsError = isApiError && error.isCorsError
  const isNetworkError = isApiError && error.isNetworkError

  // Choose icon based on error type
  const Icon = isCorsError || isNetworkError ? WifiOff : isApiError && error.status >= 500 ? ServerOff : AlertCircle

  // Default title based on error type
  const displayTitle = title || (
    isCorsError ? 'Connection Error' :
    isNetworkError ? 'Network Error' :
    isApiError && error.status >= 500 ? 'Server Error' :
    'Error'
  )

  if (compact) {
    return (
      <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm">
        <Icon className="h-4 w-4 text-red-500 flex-shrink-0" />
        <span className="text-red-700 dark:text-red-300 flex-1">{error.message}</span>
        {onRetry && (
          <Button variant="ghost" size="sm" onClick={onRetry} className="text-red-600">
            <RefreshCw className="h-3 w-3" />
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
        <Icon className="h-8 w-8 text-red-500" />
      </div>

      <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
        {displayTitle}
      </h3>

      <p className="text-slate-600 dark:text-slate-400 max-w-md mb-4">
        {error.message}
      </p>

      {/* Additional context for CORS errors */}
      {isCorsError && (
        <p className="text-xs text-slate-500 dark:text-slate-500 max-w-md mb-4">
          The API server may need to allow requests from this origin. Check the browser console for details.
        </p>
      )}

      {/* Error code for debugging */}
      {isApiError && error.code && (
        <p className="text-xs text-slate-400 dark:text-slate-500 font-mono mb-4">
          Error code: {error.code}
        </p>
      )}

      {onRetry && (
        <Button variant="primary" onClick={onRetry}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
      )}
    </div>
  )
}

// Inline error for use within other components
export function InlineError({ error, onRetry }: { error: Error | null; onRetry?: () => void }) {
  if (!error) return null

  return (
    <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
      <AlertCircle className="h-4 w-4 flex-shrink-0" />
      <span>{error.message}</span>
      {onRetry && (
        <button
          onClick={onRetry}
          className="text-red-500 hover:text-red-600 underline text-xs"
        >
          Retry
        </button>
      )}
    </div>
  )
}
